import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';
import { notifyError } from '../notify';
import FleetTokens from '../components/FleetTokens';


function Users({ role }) {
  const [users, setUsers] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('poster_va');
  const [newOrgId, setNewOrgId] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [editingPassword, setEditingPassword] = useState(null);
  const [newPasswordValue, setNewPasswordValue] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchOrgs();
  }, []);

  const fetchOrgs = async () => {
    try {
      const res = await axios.get(`${API}/api/organizations`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setOrganizations(res.data);
    } catch (err) {
      // Not super_admin, can't fetch orgs — that's fine
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/api/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setUsers(res.data);
    } catch (err) {
      notifyError(err, 'Could not load users');
    }
  };

  const addUser = async () => {
    if (!newUsername || !newPassword) return;
    setLoading(true);
    try {
      // sent over HTTPS and hashed on the server (which enforces 8+ characters)
      await axios.post(`${API}/api/users`, {
        username: newUsername,
        password: newPassword,
        role: newRole,
        organization_id: newOrgId || undefined
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setNewUsername('');
      setNewPassword('');
      setNewRole('poster_va');
      fetchUsers();
    } catch (err) {
      notifyError(err, 'Could not add user');
    }
    setLoading(false);
  };

  const updateRole = async (id, role) => {
    try {
      await axios.put(`${API}/api/users/${id}`, { role }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setEditingRole(null);
      fetchUsers();
    } catch (err) {
      notifyError(err, 'Could not update role');
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await axios.delete(`${API}/api/users/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      fetchUsers();
    } catch (err) {
      notifyError(err, 'Could not delete user');
    }
  };

  const toggleActive = async (id, currentActive) => {
    try {
      await axios.put(`${API}/api/users/${id}/toggle-active`, { active: !currentActive }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      fetchUsers();
    } catch (err) {
      notifyError(err, 'Could not change user status');
    }
  };

  const changePassword = async (id) => {
    if (!newPasswordValue.trim()) return;
    try {
      await axios.put(`${API}/api/users/${id}`, { password: newPasswordValue }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setEditingPassword(null);
      setNewPasswordValue('');
    } catch (err) {
      notifyError(err, 'Could not change password');
    }
  };

  const getRoleLabel = (role) => {
    if (role === 'super_admin') return '🔒 Super Admin';
    if (role === 'admin') return '👑 Admin';
    if (role === 'head_va') return '⭐ Head VA';
    if (role === 'poster_va') return '📋 Poster VA';
    return role;
  };

  const getRoleColor = (role) => {
    if (role === 'admin') return '#ff4500';
    if (role === 'head_va') return '#f59e0b';
    if (role === 'poster_va') return '#3b82f6';
    return '#aaaaaa';
  };

  return (
    <div className="page">
      <h1>Users</h1>
      <p>Manage user accounts and roles.</p>

      <div className="card">
        <h2>Add New User</h2>
        <div className="form-grid">
          <div className="field-group">
            <label>Username</label>
            <input
              placeholder="Enter username"
              value={newUsername}
              onChange={e => setNewUsername(e.target.value)}
            />
          </div>
          <div className="field-group">
            <label>Password</label>
            <input
              type="password"
              placeholder="Enter password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
            />
          </div>
          <div className="field-group">
            <label>Role</label>
            <select value={newRole} onChange={e => setNewRole(e.target.value)}>
              <option value="poster_va">Poster VA</option>
              <option value="head_va">Head VA</option>
              {role === 'super_admin' && <option value="admin">Admin</option>}
            </select>
          </div>
          {role === 'super_admin' && organizations.length > 0 && (
            <div className="field-group">
              <label>Organization</label>
              <select value={newOrgId} onChange={e => setNewOrgId(e.target.value)}>
                <option value="">— Select organization —</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="form-actions">
          <button onClick={addUser} disabled={loading}>
            {loading ? 'Adding...' : 'Add User'}
          </button>
        </div>
      </div>

      {role === 'super_admin' && (
        <div className="card">
          <h2>Organizations</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
            {organizations.map(org => (
              <div key={org.id} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                background: '#0f0f0f', borderRadius: '8px', padding: '10px 14px',
                border: '1px solid #2a2a2a'
              }}>
                <span style={{ fontSize: '14px' }}>🏢</span>
                <span style={{ flex: 1, fontSize: '13px', color: '#fff', fontWeight: '600' }}>{org.name}</span>
                <button className="btn-secondary" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => {
                  const name = prompt('Rename organization:', org.name);
                  if (name?.trim()) {
                    axios.put(`${API}/api/organizations/${org.id}`, { name: name.trim() }, {
                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                    }).then(() => fetchOrgs()).catch(err => notifyError(err, 'Could not rename organization'));
                  }
                }}>Rename</button>
                <button className="btn-danger" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => {
                  if (!window.confirm(`Deactivate organization "${org.name}"? This will NOT delete data.`)) return;
                  alert('Organization deactivation not yet implemented. Contact developer.');
                }}>Deactivate</button>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              placeholder="New organization name..."
              id="newOrgName"
              style={{ flex: 1 }}
              onKeyDown={e => { if (e.key === 'Enter') document.getElementById('createOrgBtn').click(); }}
            />
            <button id="createOrgBtn" onClick={() => {
              const name = document.getElementById('newOrgName').value;
              if (!name?.trim()) return;
              axios.post(`${API}/api/organizations`, { name: name.trim() }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
              }).then(() => { document.getElementById('newOrgName').value = ''; fetchOrgs(); })
                .catch(err => notifyError(err, 'Could not create organization'));
            }}>+ Create Organization</button>
          </div>
        </div>
      )}

      <FleetTokens role={role} organizations={organizations} />

      <div className="card">
        <h2>All Users</h2>
        {users.length === 0 ? (
          <p className="empty">No users found.</p>
        ) : (
          <div className="list">
            {users.map(user => (
              <div className="list-item" key={user.id}>
                <div>
                  <strong>{user.username}</strong>
                  <span style={{
                    marginLeft: '10px', fontSize: '11px', padding: '2px 8px',
                    borderRadius: '10px', background: getRoleColor(user.role),
                    color: '#fff', fontWeight: '600'
                  }}>
                    {getRoleLabel(user.role)}
                  </span>
                  {role === 'super_admin' && user.organization_id && (
                    <span style={{ marginLeft: '10px', fontSize: '10px', color: '#555', background: '#1a1a1a', padding: '2px 6px', borderRadius: '6px' }}>
                      {organizations.find(o => o.id === user.organization_id)?.name || 'Unknown org'}
                    </span>
                  )}
                  {user.active === false && (
                    <span style={{ marginLeft: '10px', fontSize: '10px', color: '#ef4444', background: '#2a0a0a', padding: '2px 6px', borderRadius: '6px' }}>
                      DEACTIVATED
                    </span>
                  )}
                  <span className="sub" style={{ marginLeft: '12px' }}>Added {new Date(user.created_at).toLocaleDateString()}</span>
                </div>
                <div className="list-item-actions">
                    {editingPassword === user.id ? (
                      <>
                        <input
                          type="password"
                          placeholder="New password"
                          value={newPasswordValue}
                          onChange={e => setNewPasswordValue(e.target.value)}
                          style={{ width: '150px' }}
                        />
                        <button onClick={() => changePassword(user.id)}>Save</button>
                        <button className="btn-secondary" onClick={() => { setEditingPassword(null); setNewPasswordValue(''); }}>Cancel</button>
                      </>
                    ) : editingRole === user.id ? (
                      <>
                        <select
                          defaultValue={user.role}
                          onChange={e => updateRole(user.id, e.target.value)}
                        >
                          <option value="poster_va">Poster VA</option>
                          <option value="head_va">Head VA</option>
                          <option value="admin">Admin</option>
                        </select>
                        <button className="btn-secondary" onClick={() => setEditingRole(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        {user.username !== 'rice' && (
                          <button className="btn-secondary" onClick={() => setEditingRole(user.id)}>Change Role</button>
                        )}
                        <button className="btn-secondary" onClick={() => { setEditingPassword(user.id); setNewPasswordValue(''); }}>Change Password</button>
                        {user.username !== 'rice' && (
                          <button
                            className="btn-secondary"
                            style={{ fontSize: '11px', color: user.active === false ? '#22c55e' : '#f59e0b' }}
                            onClick={() => toggleActive(user.id, user.active !== false)}
                          >{user.active === false ? 'Activate' : 'Deactivate'}</button>
                        )}
                        {user.username !== 'rice' && (
                          <button className="btn-danger" onClick={() => deleteUser(user.id)}>Delete</button>
                        )}
                      </>
                    )}
                  </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Users;